import { supabase } from '../../lib/supabase';
import { logger } from '../loggingService';

export interface JiraTicket {
  id: string;
  key: string;
  summary: string;
  description: string;
  status: string;
  priority: string;
  assignee?: string;
  reporter?: string;
  created: string;
  updated: string;
  storyPoints?: number;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  description?: string;
  lead?: {
    accountId: string;
    displayName: string;
  };
  projectTypeKey?: string;
  projectCategory?: {
    id: string;
    name: string;
  };
  url?: string;
  avatarUrls?: {
    '16x16': string;
    '24x24': string;
    '32x32': string;
    '48x48': string;
  };
}

export interface JiraWebhookEvent {
  eventType: string;
  issue: {
    id: string;
    key: string;
    fields: {
      summary: string;
      description: string;
      status: {
        name: string;
      };
      assignee?: {
        displayName: string;
      };
      priority: {
        name: string;
      };
      labels: string[];
    };
  };
  user: {
    displayName: string;
  };
  timestamp: string;
  project_id?: string;
}

export interface JiraSyncStatus {
  synced: boolean;
  lastSync: string;
  totalTickets: number;
  todoTickets: number;
  inProgressTickets: number;
  doneTickets: number;
  errors: string[];
}

export interface JiraEpic {
  id: string;
  key: string;
  summary: string;
  description: string;
  status: string;
  priority: string;
  assignee?: string;
  labels: string[];
  customfield_10014?: number;
}

export interface JiraSprint {
  id: number;
  name: string;
  state: 'future' | 'active' | 'closed';
  startDate?: string;
  endDate?: string;
  goal?: string;
}

export interface JiraIssueType {
  id: string;
  name: string;
  description: string;
  iconUrl: string;
  subtask: boolean;
  hierarchyLevel: number;
}

export interface CreateJiraProjectOptions {
  projectName: string;
  projectKey: string;
  description: string;
  leadAccountId: string;
  projectTypeKey?: string;
  projectCategoryId?: string;
  assigneeType?: 'PROJECT_LEAD' | 'UNASSIGNED';
}

export interface LinkJiraProjectOptions {
  cycloneProjectId: string;
  jiraProjectKey: string;
  jiraProjectId: string;
}

export interface PushProjectPlanOptions {
  projectId: string;
  epics: Array<{
    name: string;
    description: string;
    priority: string;
    assignee?: string;
    labels?: string[];
  }>;
  sprints: Array<{
    name: string;
    goal: string;
    startDate: string;
    endDate: string;
  }>;
  tickets: Array<{
    summary: string;
    description: string;
    issueType: string;
    priority: string;
    epicKey?: string;
    sprintId?: number;
    assignee?: string;
    labels?: string[];
    storyPoints?: number;
  }>;
}

export interface JiraSyncOptions {
  userId: string;
  ticketId: string;
  autoTransition?: boolean;
  addComment?: boolean;
  logWork?: boolean;
}

export class JiraService {
  /**
   * Get Jira access token for user
   */
  private static async getAccessToken(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select('access_token')
        .eq('user_id', userId)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        logger.error('Error fetching Jira connection', { error, userId });
        throw error;
      }

      if (!data) {
        logger.warn('No active Jira connection found');
        return null;
      }

      return data.access_token;
    } catch (error) {
      logger.error('Error getting Jira access token', { error });
      return null;
    }
  }

  /**
   * Get Jira cloud ID
   */
  private static async getCloudId(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select('metadata')
        .eq('user_id', userId)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        logger.error('Error fetching Jira cloud ID', { error, userId });
        return null;
      }

      if (!data) {
        return null;
      }

      return data.metadata?.cloudId || null;
    } catch (error) {
      logger.error('Error getting Jira cloud ID', { error });
      return null;
    }
  }

  /**
   * Create ticket in Jira
   */
  static async createTicket(
    userId: string,
    projectKey: string,
    data: {
      summary: string;
      description: string;
      issueType?: string;
      priority?: string;
      storyPoints?: number;
    }
  ): Promise<{ ticket: JiraTicket | null; error: string | null }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { ticket: null, error: 'Jira not connected' };
      }

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              project: { key: projectKey },
              summary: data.summary,
              description: {
                type: 'doc',
                version: 1,
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      {
                        type: 'text',
                        text: data.description,
                      },
                    ],
                  },
                ],
              },
              issuetype: { name: data.issueType || 'Task' },
              priority: data.priority ? { name: data.priority } : undefined,
              customfield_10016: data.storyPoints, // Story points field
            },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Jira API error', { error });
        return { ticket: null, error: 'Failed to create Jira ticket' };
      }

      const result = await response.json();

      const ticket: JiraTicket = {
        id: result.id,
        key: result.key,
        summary: data.summary,
        description: data.description,
        status: 'To Do',
        priority: data.priority || 'Medium',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        storyPoints: data.storyPoints,
      };

      return { ticket, error: null };
    } catch (error) {
      logger.error('Error creating Jira ticket', { error });
      return {
        ticket: null,
        error:
          error instanceof Error ? error.message : 'Failed to create ticket',
      };
    }
  }

  /**
   * Sync tickets from Jira
   */
  static async syncTickets(
    userId: string,
    projectKey: string
  ): Promise<{ tickets: JiraTicket[]; error: string | null }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { tickets: [], error: 'Jira not connected' };
      }

      const jql = `project = ${projectKey} ORDER BY created DESC`;
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/search?jql=${encodeURIComponent(jql)}&maxResults=100`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Jira API error', { error });
        return { tickets: [], error: 'Failed to sync tickets' };
      }

      const result = await response.json();

      const tickets: JiraTicket[] = result.issues.map((issue: any) => ({
        id: issue.id,
        key: issue.key,
        summary: issue.fields.summary,
        description: this.extractDescription(issue.fields.description),
        status: issue.fields.status.name,
        priority: issue.fields.priority?.name || 'Medium',
        assignee: issue.fields.assignee?.displayName,
        reporter: issue.fields.reporter?.displayName,
        created: issue.fields.created,
        updated: issue.fields.updated,
        storyPoints: issue.fields.customfield_10016,
      }));

      return { tickets, error: null };
    } catch (error) {
      logger.error('Error syncing Jira tickets', { error });
      return {
        tickets: [],
        error:
          error instanceof Error ? error.message : 'Failed to sync tickets',
      };
    }
  }

  /**
   * Update ticket status in Jira
   */
  static async updateTicketStatus(
    userId: string,
    issueKey: string,
    transitionName: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      // Get available transitions
      const transitionsResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${issueKey}/transitions`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!transitionsResponse.ok) {
        return { success: false, error: 'Failed to get transitions' };
      }

      const transitionsData = await transitionsResponse.json();
      const transition = transitionsData.transitions.find(
        (t: any) => t.name.toLowerCase() === transitionName.toLowerCase()
      );

      if (!transition) {
        return {
          success: false,
          error: `Transition "${transitionName}" not found`,
        };
      }

      // Execute transition
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${issueKey}/transitions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transition: { id: transition.id },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Jira API error', { error });
        return { success: false, error: 'Failed to update status' };
      }

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error updating Jira ticket', { error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to update ticket',
      };
    }
  }

  /**
   * Sync ticket to database
   */
  static async syncTicketToDatabase(
    userId: string,
    projectId: string,
    jiraTicket: JiraTicket
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      // Use upsert to handle race conditions atomically
      // This prevents duplicate tickets if webhook fires multiple times
      const { error } = await supabase.from('tickets').upsert(
        {
          project_id: projectId,
          user_id: userId,
          jira_key: jiraTicket.key,
          title: jiraTicket.summary,
          description: jiraTicket.description,
          status: this.mapJiraStatus(jiraTicket.status),
          priority: jiraTicket.priority.toLowerCase(),
          story_points: jiraTicket.storyPoints,
          dor_completed: false,
          dod_completed: false,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'jira_key,project_id', // Use unique constraint if exists
          ignoreDuplicates: false,
        }
      );

      if (error) {
        // If unique constraint doesn't exist, fall back to check-then-insert
        if (
          error.code === '42P01' ||
          error.message.includes('unique constraint')
        ) {
          // Try the old method as fallback
          const { data: existing } = await supabase
            .from('tickets')
            .select('id')
            .eq('jira_key', jiraTicket.key)
            .eq('project_id', projectId)
            .maybeSingle();

          if (existing) {
            const { error: updateError } = await supabase
              .from('tickets')
              .update({
                title: jiraTicket.summary,
                description: jiraTicket.description,
                status: this.mapJiraStatus(jiraTicket.status),
                priority: jiraTicket.priority.toLowerCase(),
                story_points: jiraTicket.storyPoints,
                updated_at: new Date().toISOString(),
              })
              .eq('id', existing.id);

            if (updateError) throw updateError;
          } else {
            const { error: insertError } = await supabase
              .from('tickets')
              .insert({
                project_id: projectId,
                user_id: userId,
                jira_key: jiraTicket.key,
                title: jiraTicket.summary,
                description: jiraTicket.description,
                status: this.mapJiraStatus(jiraTicket.status),
                priority: jiraTicket.priority.toLowerCase(),
                story_points: jiraTicket.storyPoints,
                dor_completed: false,
                dod_completed: false,
              });

            if (insertError) throw insertError;
          }
        } else {
          throw error;
        }
      }

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error syncing ticket to database', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sync ticket',
      };
    }
  }

  /**
   * Map Jira status to internal status
   */
  private static mapJiraStatus(jiraStatus: string): string {
    const statusMap: Record<string, string> = {
      'to do': 'todo',
      todo: 'todo',
      'in progress': 'in_progress',
      'in-progress': 'in_progress',
      inprogress: 'in_progress',
      done: 'done',
      completed: 'done',
      closed: 'done',
      'in review': 'in_review',
      'in-review': 'in_review',
      inreview: 'in_review',
      'code review': 'in_review',
      testing: 'in_review',
      blocked: 'blocked',
      cancelled: 'cancelled',
      canceled: 'cancelled',
      rejected: 'cancelled',
    };

    const normalizedStatus = jiraStatus.toLowerCase().trim();
    const mappedStatus = statusMap[normalizedStatus];

    if (!mappedStatus) {
      // Log unknown status for configuration review
      logger.warn('Unknown Jira status encountered, defaulting to todo', {
        originalStatus: jiraStatus,
        normalizedStatus,
        suggestion:
          'Add mapping for this status in JiraService.mapJiraStatus()',
      });
      return 'todo'; // Default fallback
    }

    return mappedStatus;
  }

  /**
   * Map internal Cyclone status to Jira status/transition
   */
  private static mapCycloneStatusToJira(cycloneStatus: string): string {
    const statusMap: Record<string, string> = {
      todo: 'To Do',
      in_progress: 'In Progress',
      in_review: 'In Review',
      done: 'Done',
      blocked: 'Blocked',
      cancelled: 'Cancelled',
    };

    return statusMap[cycloneStatus] || 'To Do';
  }

  /**
   * Bidirectional sync - Push local ticket updates to Jira
   */
  static async pushTicketToJira(
    userId: string,
    ticketId: string,
    updates: {
      summary?: string;
      description?: string;
      status?: string;
      priority?: string;
      storyPoints?: number;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get ticket with Jira key
      const { data: ticket, error: fetchError } = await supabase
        .from('tickets')
        .select('jira_key, project_id')
        .eq('id', ticketId)
        .single();

      if (fetchError || !ticket || !ticket.jira_key) {
        return { success: false, error: 'Ticket not linked to Jira' };
      }

      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      // Build update payload
      const fields: any = {};
      
      if (updates.summary) {
        fields.summary = updates.summary;
      }
      
      if (updates.description) {
        fields.description = {
          type: 'doc',
          version: 1,
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: updates.description }],
            },
          ],
        };
      }
      
      if (updates.priority) {
        fields.priority = { name: updates.priority };
      }
      
      if (updates.storyPoints !== undefined) {
        fields.customfield_10016 = updates.storyPoints;
      }

      // Update Jira ticket
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${ticket.jira_key}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fields }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to update Jira ticket', { error, ticketId });
        return { success: false, error: 'Failed to push updates to Jira' };
      }

      // Handle status transition separately if provided
      if (updates.status) {
        const jiraStatus = this.mapCycloneStatusToJira(updates.status);
        await this.updateTicketStatus(userId, ticket.jira_key, jiraStatus);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error pushing ticket to Jira', { error, ticketId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to push to Jira',
      };
    }
  }

  /**
   * Enable/disable automatic bidirectional sync for a project
   */
  static async configureAutoSync(
    userId: string,
    projectId: string,
    enabled: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ jira_auto_sync: enabled, updated_at: new Date().toISOString() })
        .eq('id', projectId)
        .eq('user_id', userId);

      if (error) {
        return { success: false, error: 'Failed to configure auto sync' };
      }

      logger.info('Jira auto sync configured', { projectId, enabled });
      return { success: true };
    } catch (error) {
      logger.error('Error configuring auto sync', { error, projectId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Configuration failed',
      };
    }
  }

  /**
   * Get ticket by ID (fetches from database first to get Jira key, then from Jira API)
   */
  static async getTicket(ticketId: string): Promise<{ workLogged?: number; status?: string; key?: string } | null> {
    try {
      // First, get the ticket from database to find Jira key
      const { data: ticket, error: ticketError } = await supabase
        .from('tickets')
        .select('jira_key, jira_worklog, worklog_total, status, project_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (ticketError || !ticket) {
        logger.warn('Ticket not found in database', { ticketId, error: ticketError });
        return null;
      }

      // If no Jira key, return database data only
      if (!ticket.jira_key) {
        return {
          workLogged: ticket.worklog_total || ticket.jira_worklog || 0,
          status: ticket.status || 'todo',
        };
      }

      // Try to get work logged from Jira API if we have connection info
      try {
        // Get project to find userId for API access
        const { data: project } = await supabase
          .from('projects')
          .select('user_id')
          .eq('id', ticket.project_id)
          .maybeSingle();

        if (project?.user_id) {
          const accessToken = await this.getAccessToken(project.user_id);
          const cloudId = await this.getCloudId(project.user_id);

          if (accessToken && cloudId) {
            // Fetch work logs from Jira
            const worklogResponse = await fetch(
              `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${ticket.jira_key}/worklog`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  Accept: 'application/json',
                },
              }
            );

            if (worklogResponse.ok) {
              const worklogData = await worklogResponse.json();
              const totalWorkLogged = worklogData.worklogs?.reduce(
                (sum: number, log: any) => sum + (log.timeSpentSeconds || 0),
                0
              ) || 0;

              return {
                workLogged: Math.round(totalWorkLogged / 60), // Convert seconds to minutes
                status: ticket.status || 'todo',
                key: ticket.jira_key,
              };
            }
          }
        }
      } catch (jiraError) {
        logger.debug('Could not fetch work log from Jira API', { error: jiraError, ticketId });
      }

      // Fallback to database values
      return {
        workLogged: ticket.worklog_total || ticket.jira_worklog || 0,
        status: ticket.status || 'todo',
        key: ticket.jira_key,
      };
    } catch (error) {
      logger.error('Error getting ticket', { error, ticketId });
      return null;
    }
  }

  /**
   * Extract description from Jira's ADF format
   */
  private static extractDescription(description: any): string {
    if (!description) return '';

    if (typeof description === 'string') return description;

    // Extract text from Atlassian Document Format
    if (description.content) {
      return description.content
        .map((node: any) => {
          if (node.content) {
            return node.content
              .map((textNode: any) => textNode.text || '')
              .join('');
          }
          return '';
        })
        .join('\n');
    }

    return '';
  }

  /**
   * Get Jira connection for user
   */
  static async getConnection(userId: string): Promise<{ data: any } | null> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        logger.error('Error fetching Jira connection', { error, userId });
        return null;
      }

      if (!data) {
        return null;
      }

      return { data };
    } catch (error) {
      logger.error('Error getting Jira connection', { error });
      return null;
    }
  }

  /**
   * Create complete Jira project with epics, stories, and sprints
   */
  static async createCompleteProject(
    projectKey: string,
    epics: any[],
    accessToken: string
  ): Promise<void> {
    try {
      // Get cloud ID from access token metadata
      const { data: connection, error: connectionError } = await supabase
        .from('user_connections')
        .select('metadata')
        .eq('access_token', accessToken)
        .eq('provider', 'jira')
        .maybeSingle();

      if (connectionError) {
        logger.error('Error fetching Jira connection by token', {
          error: connectionError,
        });
        throw new Error(
          `Failed to fetch connection: ${connectionError.message}`
        );
      }

      if (!connection?.metadata?.cloudId) {
        throw new Error('Jira cloud ID not found');
      }

      const cloudId = connection.metadata.cloudId;

      // Create epics
      for (const epic of epics) {
        const epicResponse = await fetch(
          `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fields: {
                project: { key: projectKey },
                summary: epic.title,
                description: {
                  type: 'doc',
                  version: 1,
                  content: [
                    {
                      type: 'paragraph',
                      content: [
                        {
                          type: 'text',
                          text: epic.description,
                        },
                      ],
                    },
                  ],
                },
                issuetype: { name: 'Epic' },
                priority: { name: epic.priority || 'Medium' },
                customfield_10014: epic.title, // Epic name field
              },
            }),
          }
        );

        if (!epicResponse.ok) {
          const error = await epicResponse.text();
          logger.error('Failed to create epic', { error });
          continue;
        }

        const epicData = await epicResponse.json();
        const epicKey = epicData.key;

        // Create stories under this epic
        for (const story of epic.stories || []) {
          const storyResponse = await fetch(
            `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                fields: {
                  project: { key: projectKey },
                  summary: story.title,
                  description: {
                    type: 'doc',
                    version: 1,
                    content: [
                      {
                        type: 'paragraph',
                        content: [
                          {
                            type: 'text',
                            text: story.description,
                          },
                        ],
                      },
                    ],
                  },
                  issuetype: { name: 'Story' },
                  priority: { name: story.priority || 'Medium' },
                  customfield_10016: story.storyPoints, // Story points
                  parent: { key: epicKey }, // Link to epic
                },
              }),
            }
          );

          if (!storyResponse.ok) {
            const error = await storyResponse.text();
            logger.error('Failed to create story', { error });
            continue;
          }
        }
      }

      logger.info('Jira project creation completed', {
        projectKey,
        epicsCount: epics.length,
      });
    } catch (error) {
      logger.error('Error creating Jira project', { error });
      throw error;
    }
  }

  /**
   * Mark blueprint as synced with Jira
   */
  static async markBlueprintAsSynced(
    blueprintId: string,
    jiraProjectKey: string
  ): Promise<void> {
    const { error } = await supabase
      .from('project_blueprints')
      .update({
        jira_synced: true,
        jira_project_key: jiraProjectKey,
      })
      .eq('id', blueprintId);

    if (error) throw error;
  }

  /**
   * Add work log entry
   */
  static async addWorkLog(
    ticketId: string,
    agentName: string,
    timeSpent: number,
    description: string
  ): Promise<void> {
    try {
      // Get connection details
      const { data: connection, error: connectionError } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (connectionError) {
        logger.error('Error fetching Jira connection for work log', {
          error: connectionError,
        });
        throw new Error(
          `Failed to fetch connection: ${connectionError.message}`
        );
      }

      if (!connection) {
        throw new Error('No active Jira connection found');
      }

      const { access_token, metadata } = connection;
      const cloudId = metadata?.cloudId;

      if (!cloudId) {
        throw new Error('Jira cloud ID not found');
      }

      // Add work log
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${ticketId}/worklog`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${access_token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timeSpent: `${timeSpent}m`,
            comment: {
              type: 'doc',
              version: 1,
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: `${description} (by ${agentName})`,
                    },
                  ],
                },
              ],
            },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to add work log: ${error}`);
      }

      logger.info('Work log added successfully', {
        ticketId,
        agentName,
        timeSpent,
      });
    } catch (error) {
      logger.error('Error adding work log', { error });
      throw error;
    }
  }

  /**
   * Create sub-task for revisions
   */
  static async createRevisionSubTask(
    parentTicketId: string,
    issues: string[]
  ): Promise<string> {
    try {
      // Get connection details
      const { data: connection, error: connectionError } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (connectionError) {
        logger.error('Error fetching Jira connection for work log', {
          error: connectionError,
        });
        throw new Error(
          `Failed to fetch connection: ${connectionError.message}`
        );
      }

      if (!connection) {
        throw new Error('No active Jira connection found');
      }

      const { access_token, metadata } = connection;
      const cloudId = metadata?.cloudId;

      if (!cloudId) {
        throw new Error('Jira cloud ID not found');
      }

      // Get parent ticket details to extract project key
      const parentResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${parentTicketId}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            Accept: 'application/json',
          },
        }
      );

      if (!parentResponse.ok) {
        throw new Error('Failed to get parent ticket details');
      }

      const parentData = await parentResponse.json();
      const projectKey = parentData.fields.project.key;

      // Create revision sub-task
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${access_token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              project: { key: projectKey },
              summary: `Revision Required: ${issues.join(', ')}`,
              description: {
                type: 'doc',
                version: 1,
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      {
                        type: 'text',
                        text: `Revision required for the following issues:\n\n${issues.map(issue => `- ${issue}`).join('\n')}`,
                      },
                    ],
                  },
                ],
              },
              issuetype: { name: 'Sub-task' },
              parent: { key: parentTicketId },
              priority: { name: 'High' },
            },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to create revision sub-task: ${error}`);
      }

      const result = await response.json();
      logger.info('Revision sub-task created successfully', {
        parentTicketId,
        subTaskKey: result.key,
      });
      return result.key;
    } catch (error) {
      logger.error('Error creating revision sub-task', { error });
      throw error;
    }
  }

  /**
   * Move ticket to next sprint
   */
  static async moveTicketToSprint(
    ticketId: string,
    sprintId: string
  ): Promise<void> {
    try {
      // Get connection details
      const { data: connection, error: connectionError } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (connectionError) {
        logger.error('Error fetching Jira connection for work log', {
          error: connectionError,
        });
        throw new Error(
          `Failed to fetch connection: ${connectionError.message}`
        );
      }

      if (!connection) {
        throw new Error('No active Jira connection found');
      }

      const { access_token, metadata } = connection;
      const cloudId = metadata?.cloudId;

      if (!cloudId) {
        throw new Error('Jira cloud ID not found');
      }

      // Move ticket to sprint
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/sprint/${sprintId}/issue`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${access_token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            issues: [ticketId],
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to move ticket to sprint: ${error}`);
      }

      logger.info('Ticket moved to sprint successfully', {
        ticketId,
        sprintId,
      });
    } catch (error) {
      logger.error('Error moving ticket to sprint', { error });
      throw error;
    }
  }

  /**
   * Sync latest updates from Jira to Cyclone
   */
  static async syncFromJira(
    userId: string,
    projectId: string,
    jiraProjectKey: string
  ): Promise<{ success: boolean; issuesUpdated?: number; error?: string }> {
    try {
      const { tickets, error } = await this.syncTickets(userId, jiraProjectKey);

      if (error) {
        return { success: false, error };
      }

      // Update tickets in our database
      let updatedCount = 0;
      for (const jiraTicket of tickets) {
        const { error: upsertError } = await supabase.from('tickets').upsert(
          {
            project_id: projectId,
            jira_ticket_id: jiraTicket.key,
            title: jiraTicket.summary,
            description: jiraTicket.description,
            status: this.mapJiraStatusToCyclone(jiraTicket.status),
            priority: jiraTicket.priority.toLowerCase(),
            updated_at: jiraTicket.updated,
          },
          {
            onConflict: 'jira_ticket_id',
          }
        );

        if (!upsertError) {
          updatedCount++;
        }
      }

      await supabase
        .from('projects')
        .update({
          last_jira_sync: new Date().toISOString(),
        })
        .eq('id', projectId);

      logger.info('Jira sync completed', {
        projectId,
        jiraProjectKey,
        issuesUpdated: updatedCount,
      });

      return { success: true, issuesUpdated: updatedCount };
    } catch (error) {
      logger.error('Jira sync failed', { error, projectId });
      return { success: false, error: 'Failed to sync from Jira' };
    }
  }

  /**
   * Push local changes to Jira
   */
  static async pushToJira(
    userId: string,
    projectId: string,
    jiraProjectKey: string
  ): Promise<{ success: boolean; issuesCreated?: number; error?: string }> {
    try {
      // Get tickets that don't have Jira ticket IDs
      const { data: localTickets } = await supabase
        .from('tickets')
        .select('*')
        .eq('project_id', projectId)
        .is('jira_ticket_id', null);

      if (!localTickets || localTickets.length === 0) {
        return { success: true, issuesCreated: 0 };
      }

      let createdCount = 0;
      for (const ticket of localTickets) {
        const { ticket: jiraTicket, error } = await this.createTicket(
          userId,
          jiraProjectKey,
          {
            summary: ticket.title,
            description: ticket.description || '',
            issueType: 'Task',
            priority: ticket.priority || 'Medium',
          }
        );

        if (jiraTicket && !error) {
          // Update local ticket with Jira ID
          await supabase
            .from('tickets')
            .update({ jira_ticket_id: jiraTicket.key })
            .eq('id', ticket.id);

          createdCount++;
        }
      }

      await supabase
        .from('projects')
        .update({
          last_jira_push: new Date().toISOString(),
        })
        .eq('id', projectId);

      logger.info('Jira push completed', {
        projectId,
        jiraProjectKey,
        issuesCreated: createdCount,
      });

      return { success: true, issuesCreated: createdCount };
    } catch (error) {
      logger.error('Jira push failed', { error, projectId });
      return { success: false, error: 'Failed to push to Jira' };
    }
  }

  /**
   * Create tasks from blueprint sections
   */
  static async createTasksFromBlueprint(
    userId: string,
    projectId: string,
    jiraProjectKey: string,
    blueprintId: string
  ): Promise<{ success: boolean; tasksCreated?: number; error?: string }> {
    try {
      // Get blueprint data
      const { data: blueprint, error: blueprintError } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .maybeSingle();

      if (blueprintError) {
        logger.error('Error fetching blueprint for Jira task creation', {
          error: blueprintError,
          blueprintId,
        });
        return {
          success: false,
          error: `Failed to fetch blueprint: ${blueprintError.message}`,
        };
      }

      if (!blueprint) {
        return { success: false, error: 'Blueprint not found' };
      }

      // Get features and requirements
      const { data: features } = await supabase
        .from('features')
        .select('*')
        .eq('project_id', projectId);

      const { data: requirements } = await supabase
        .from('atomic_requirements')
        .select('*')
        .eq('project_id', projectId);

      let tasksCreated = 0;

      // Create epics for features
      if (features && features.length > 0) {
        for (const feature of features) {
          const { ticket, error } = await this.createTicket(
            userId,
            jiraProjectKey,
            {
              summary: feature.name,
              description: feature.description || '',
              issueType: 'Epic',
              priority: 'Medium',
            }
          );

          if (ticket && !error) {
            // Store epic mapping
            await supabase
              .from('features')
              .update({ jira_epic_key: ticket.key })
              .eq('id', feature.id);

            tasksCreated++;
          }
        }
      }

      // Create stories for requirements
      if (requirements && requirements.length > 0) {
        for (const req of requirements) {
          const { ticket, error } = await this.createTicket(
            userId,
            jiraProjectKey,
            {
              summary: req.title,
              description: req.description || '',
              issueType: 'Story',
              priority: req.priority || 'Medium',
            }
          );

          if (ticket && !error) {
            await supabase
              .from('atomic_requirements')
              .update({ jira_story_key: ticket.key })
              .eq('id', req.id);

            tasksCreated++;
          }
        }
      }

      logger.info('Blueprint tasks created in Jira', {
        projectId,
        jiraProjectKey,
        tasksCreated,
      });

      return { success: true, tasksCreated };
    } catch (error) {
      logger.error('Failed to create tasks from blueprint', {
        error,
        projectId,
      });
      return { success: false, error: 'Failed to create tasks from blueprint' };
    }
  }

  /**
   * Handle Jira webhook events
   */
  static async handleWebhook(
    event: string,
    payload: any,
    projectId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('Jira webhook received', { event, projectId });

      const issue = payload.issue;
      if (!issue) {
        return { success: false, error: 'No issue data in payload' };
      }

      switch (event) {
        case 'jira:issue_created':
        case 'jira:issue_updated':
          // Sync issue to our database
          await supabase.from('tickets').upsert(
            {
              project_id: projectId,
              jira_ticket_id: issue.key,
              title: issue.fields.summary,
              description: this.extractDescription(issue.fields.description),
              status: this.mapJiraStatusToCyclone(issue.fields.status.name),
              priority: issue.fields.priority?.name?.toLowerCase() || 'medium',
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: 'jira_ticket_id',
            }
          );
          break;

        case 'jira:issue_deleted':
          // Mark ticket as deleted
          await supabase
            .from('tickets')
            .update({ status: 'cancelled' })
            .eq('jira_ticket_id', issue.key);
          break;

        case 'comment_created':
        case 'comment_updated':
          // Sync comment to our system
          logger.info('Jira comment event', { issueKey: issue.key });
          break;

        default:
          logger.warn('Unhandled Jira webhook event', { event });
      }

      return { success: true };
    } catch (error) {
      logger.error('Jira webhook handling failed', { error, event, projectId });
      return { success: false, error: 'Failed to handle webhook' };
    }
  }

  /**
   * Update task status bi-directionally
   */
  static async updateTaskStatus(
    userId: string,
    ticketId: string,
    newStatus: string,
    syncToJira: boolean = true
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Update in our database
      const { data: ticket, error: dbError } = await supabase
        .from('tickets')
        .update({ status: newStatus })
        .eq('id', ticketId)
        .select()
        .single();

      if (dbError || !ticket) {
        return { success: false, error: 'Failed to update ticket in database' };
      }

      // Sync to Jira if requested and ticket has Jira ID
      if (syncToJira && ticket.jira_ticket_id) {
        const jiraStatus = this.mapCycloneStatusToJira(newStatus);
        const { success, error } = await this.updateTicketStatus(
          userId,
          ticket.jira_ticket_id,
          jiraStatus
        );

        if (!success) {
          logger.warn('Failed to sync status to Jira', { error, ticketId });
          // Don't fail the whole operation if Jira sync fails
        }
      }

      return { success: true };
    } catch (error) {
      logger.error('Failed to update task status', { error, ticketId });
      return { success: false, error: 'Failed to update task status' };
    }
  }

  /**
   * Map Jira status to Cyclone status
   */
  private static mapJiraStatusToCyclone(jiraStatus: string): string {
    const statusMap: Record<string, string> = {
      'To Do': 'todo',
      'In Progress': 'in_progress',
      'In Review': 'review',
      Done: 'done',
      Closed: 'done',
      Blocked: 'blocked',
    };

    return statusMap[jiraStatus] || 'todo';
  }

  // Note: mapCycloneStatusToJira is already defined above (line 580)

  /**
   * Add comment to Jira issue
   */
  static async addComment(
    userId: string,
    issueKey: string,
    commentText: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${issueKey}/comment`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            body: {
              type: 'doc',
              version: 1,
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: commentText,
                    },
                  ],
                },
              ],
            },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to add comment to Jira', { error });
        return { success: false, error: 'Failed to add comment' };
      }

      logger.info('Comment added successfully', { issueKey });
      return { success: true };
    } catch (error) {
      logger.error('Error adding comment to Jira', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add comment',
      };
    }
  }

  /**
   * Get available transitions for an issue
   */
  static async getTransitions(
    userId: string,
    issueKey: string
  ): Promise<{
    success: boolean;
    data?: Array<{ id: string; name: string }>;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${issueKey}/transitions`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to get transitions', { error });
        return { success: false, error: 'Failed to get transitions' };
      }

      const data = await response.json();
      const transitions = data.transitions.map((t: any) => ({
        id: t.id,
        name: t.name,
      }));

      return { success: true, data: transitions };
    } catch (error) {
      logger.error('Error getting transitions', { error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to get transitions',
      };
    }
  }

  /**
   * Transition issue to new status
   */
  static async transitionIssue(
    userId: string,
    issueKey: string,
    transitionId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${issueKey}/transitions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transition: { id: transitionId },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to transition issue', { error });
        return { success: false, error: 'Failed to transition issue' };
      }

      logger.info('Issue transitioned successfully', {
        issueKey,
        transitionId,
      });
      return { success: true };
    } catch (error) {
      logger.error('Error transitioning issue', { error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to transition issue',
      };
    }
  }

  /**
   * Get all issues for a Jira project
   */
  static async getProjectIssues(
    userId: string,
    projectKey: string,
    maxResults: number = 100
  ): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      const jql = `project = ${projectKey} ORDER BY created DESC`;
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/search?jql=${encodeURIComponent(jql)}&maxResults=${maxResults}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to get project issues', { error });
        return { success: false, error: 'Failed to get project issues' };
      }

      const data = await response.json();
      logger.info('Project issues retrieved', {
        projectKey,
        count: data.issues.length,
      });

      return { success: true, data: data.issues };
    } catch (error) {
      logger.error('Error getting project issues', { error });
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to get project issues',
      };
    }
  }

  // ==========================================================================
  // Webhook Processing (from jiraBidirectionalSyncService.ts)
  // ==========================================================================

  /**
   * Process incoming Jira webhook events
   */
  static async processWebhookEvent(event: JiraWebhookEvent): Promise<void> {
    try {
      logger.info('Processing Jira webhook event', { 
        eventType: event.eventType, 
        issueKey: event.issue.key 
      });

      switch (event.eventType) {
        case 'jira:issue_created':
          await this.handleIssueCreated(event);
          break;
        case 'jira:issue_updated':
          await this.handleIssueUpdated(event);
          break;
        case 'jira:issue_deleted':
          await this.handleIssueDeleted(event);
          break;
        case 'jira:issue_commented':
          await this.handleIssueCommented(event);
          break;
        case 'jira:issue_worklog_updated':
          await this.handleWorklogUpdated(event);
          break;
        default:
          logger.warn('Unknown Jira webhook event type', { eventType: event.eventType });
      }
    } catch (error) {
      logger.error('Error processing Jira webhook event', { error, event });
      throw error;
    }
  }

  /**
   * Handle issue created event
   */
  private static async handleIssueCreated(event: JiraWebhookEvent): Promise<void> {
    try {
      const { data: existingTicket, error: fetchError } = await supabase
        .from('tickets')
        .select('id')
        .eq('jira_key', event.issue.key)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existingTicket) {
        logger.info('Ticket already exists, skipping creation', { jiraKey: event.issue.key });
        return;
      }

      const { error: insertError } = await supabase
        .from('tickets')
        .insert({
          jira_key: event.issue.key,
          title: event.issue.fields.summary,
          description: typeof event.issue.fields.description === 'string' 
            ? event.issue.fields.description 
            : this.extractDescription(event.issue.fields.description),
          status: this.mapJiraStatus(event.issue.fields.status.name),
          priority: event.issue.fields.priority?.name?.toLowerCase() || 'medium',
          labels: event.issue.fields.labels || [],
          assignee: event.issue.fields.assignee?.displayName,
          created_by: 'jira_webhook',
          updated_at: event.timestamp,
          project_id: event.project_id || null,
        });

      if (insertError) {
        throw insertError;
      }

      logger.info('Created ticket from Jira issue', { jiraKey: event.issue.key });
    } catch (error) {
      logger.error('Error handling issue created event', { error, event });
      throw error;
    }
  }

  /**
   * Handle issue updated event
   */
  private static async handleIssueUpdated(event: JiraWebhookEvent): Promise<void> {
    try {
      const { error: updateError } = await supabase
        .from('tickets')
        .update({
          title: event.issue.fields.summary,
          description: typeof event.issue.fields.description === 'string'
            ? event.issue.fields.description
            : this.extractDescription(event.issue.fields.description) || '',
          status: this.mapJiraStatus(event.issue.fields.status.name),
          priority: event.issue.fields.priority?.name?.toLowerCase() || 'medium',
          updated_at: event.timestamp
        })
        .eq('jira_key', event.issue.key);

      if (updateError) {
        throw updateError;
      }

      logger.info('Updated ticket from Jira issue', { jiraKey: event.issue.key });
    } catch (error) {
      logger.error('Error handling issue updated event', { error, event });
      throw error;
    }
  }

  /**
   * Handle issue deleted event
   */
  private static async handleIssueDeleted(event: JiraWebhookEvent): Promise<void> {
    try {
      const { error: deleteError } = await supabase
        .from('tickets')
        .update({
          status: 'cancelled',
          updated_at: event.timestamp
        })
        .eq('jira_key', event.issue.key);

      if (deleteError) {
        throw deleteError;
      }

      logger.info('Marked ticket as cancelled from Jira issue', { jiraKey: event.issue.key });
    } catch (error) {
      logger.error('Error handling issue deleted event', { error, event });
      throw error;
    }
  }

  /**
   * Handle issue commented event
   */
  private static async handleIssueCommented(event: JiraWebhookEvent): Promise<void> {
    try {
      const { error: logError } = await supabase
        .from('jira_comments')
        .insert({
          jira_key: event.issue.key,
          comment: `Comment by ${event.user.displayName}`,
          author: event.user.displayName,
          created_at: event.timestamp
        });

      if (logError) {
        logger.warn('Failed to log Jira comment', { error: logError });
      }

      logger.info('Processed Jira comment', { jiraKey: event.issue.key });
    } catch (error) {
      logger.error('Error handling issue commented event', { error, event });
      throw error;
    }
  }

  /**
   * Handle worklog updated event
   */
  private static async handleWorklogUpdated(event: JiraWebhookEvent): Promise<void> {
    try {
      const { error: logError } = await supabase
        .from('jira_worklogs')
        .insert({
          jira_key: event.issue.key,
          worklog: `Work logged by ${event.user.displayName}`,
          author: event.user.displayName,
          created_at: event.timestamp
        });

      if (logError) {
        logger.warn('Failed to log Jira worklog', { error: logError });
      }

      logger.info('Processed Jira worklog update', { jiraKey: event.issue.key });
    } catch (error) {
      logger.error('Error handling worklog updated event', { error, event });
      throw error;
    }
  }

  // ==========================================================================
  // Project Management (from jiraBidirectionalSyncService.ts)
  // ==========================================================================

  /**
   * Create a new Jira project
   */
  static async createJiraProject(options: CreateJiraProjectOptions): Promise<{
    success: boolean;
    project?: JiraProject;
    error?: string;
  }> {
    try {
      logger.info('Creating Jira project', { projectKey: options.projectKey });

      const { data: connection } = await supabase
        .from('user_connections')
        .select('*')
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (!connection) {
        return {
          success: false,
          error: 'No active Jira connection found. Please connect your Jira account first.'
        };
      }

      const cloudId = connection.metadata?.cloudId;
      if (!cloudId) {
        return {
          success: false,
          error: 'Jira cloud ID not found in connection metadata'
        };
      }

      const accessToken = connection.access_token;

      const createResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/project`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: options.projectKey,
            name: options.projectName,
            description: options.description,
            lead: options.leadAccountId,
            projectTypeKey: options.projectTypeKey || 'software',
            projectCategoryId: options.projectCategoryId,
            assigneeType: options.assigneeType || 'PROJECT_LEAD',
          }),
        }
      );

      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        logger.error('Failed to create Jira project', {
          status: createResponse.status,
          error: errorText,
          projectKey: options.projectKey,
        });
        return {
          success: false,
          error: `Failed to create Jira project: ${errorText}`,
        };
      }

      const createdProject = await createResponse.json();

      const jiraProject: JiraProject = {
        id: createdProject.id,
        key: createdProject.key,
        name: createdProject.name,
        description: createdProject.description || '',
        lead: {
          accountId: createdProject.lead?.accountId || options.leadAccountId,
          displayName: createdProject.lead?.displayName || 'Project Lead',
        },
        projectTypeKey: createdProject.projectTypeKey || options.projectTypeKey || 'software',
        projectCategory: createdProject.projectCategory ? {
          id: createdProject.projectCategory.id,
          name: createdProject.projectCategory.name,
        } : undefined,
        url: createdProject.url || '',
        avatarUrls: createdProject.avatarUrls || {
          '16x16': '',
          '24x24': '',
          '32x32': '',
          '48x48': '',
        },
      };

      logger.info('Jira project created successfully', {
        projectKey: options.projectKey,
        projectId: createdProject.id,
      });

      return {
        success: true,
        project: jiraProject,
      };
    } catch (error) {
      logger.error('Error creating Jira project', { error, projectKey: options.projectKey });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Link existing Jira project to Cyclone project
   */
  static async linkJiraProject(options: LinkJiraProjectOptions): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      logger.info('Linking Jira project', { 
        cycloneProjectId: options.cycloneProjectId, 
        jiraProjectKey: options.jiraProjectKey 
      });

      const jiraProject = await this.getJiraProject(options.jiraProjectKey);
      if (!jiraProject) {
        return {
          success: false,
          error: 'Jira project not found or access denied'
        };
      }

      const { error } = await supabase
        .from('projects')
        .update({
          jira_project_key: options.jiraProjectKey,
          jira_project_id: options.jiraProjectId,
          updated_at: new Date().toISOString()
        })
        .eq('id', options.cycloneProjectId);

      if (error) {
        logger.error('Failed to link Jira project', { error });
        return {
          success: false,
          error: 'Failed to save Jira project link'
        };
      }

      logger.info('Jira project linked successfully', { 
        cycloneProjectId: options.cycloneProjectId, 
        jiraProjectKey: options.jiraProjectKey 
      });

      return { success: true };
    } catch (error) {
      logger.error('Error linking Jira project', { error, cycloneProjectId: options.cycloneProjectId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get Jira project details
   */
  static async getJiraProject(projectKey: string, userId?: string): Promise<JiraProject | null> {
    try {
      const { data: connection } = await supabase
        .from('user_connections')
        .select('*')
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (!connection) {
        logger.warn('No active Jira connection found', { projectKey, userId });
        return null;
      }

      const cloudId = connection.metadata?.cloudId;
      if (!cloudId) {
        logger.warn('Jira cloud ID not found in connection metadata', { projectKey });
        return null;
      }

      const accessToken = connection.access_token;

      const projectResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/project/${projectKey}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!projectResponse.ok) {
        if (projectResponse.status === 404) {
          logger.warn('Jira project not found', { projectKey, status: projectResponse.status });
          return null;
        }
        const errorText = await projectResponse.text();
        logger.error('Failed to fetch Jira project', {
          status: projectResponse.status,
          error: errorText,
          projectKey,
        });
        return null;
      }

      const projectData = await projectResponse.json();

      const jiraProject: JiraProject = {
        id: projectData.id,
        key: projectData.key,
        name: projectData.name,
        description: projectData.description || '',
        lead: {
          accountId: projectData.lead?.accountId || '',
          displayName: projectData.lead?.displayName || projectData.lead?.name || 'Project Lead',
        },
        projectTypeKey: projectData.projectTypeKey || 'software',
        projectCategory: projectData.projectCategory ? {
          id: projectData.projectCategory.id,
          name: projectData.projectCategory.name,
        } : undefined,
        url: projectData.url || '',
        avatarUrls: projectData.avatarUrls || {
          '16x16': '',
          '24x24': '',
          '32x32': '',
          '48x48': '',
        },
      };

      logger.debug('Jira project fetched successfully', { projectKey, projectId: projectData.id });
      return jiraProject;
    } catch (error) {
      logger.error('Error getting Jira project', { error, projectKey });
      return null;
    }
  }

  /**
   * Get Jira issue types for project
   */
  static async getJiraIssueTypes(projectKey: string): Promise<JiraIssueType[]> {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('user_id, jira_project_id')
        .eq('jira_project_key', projectKey)
        .maybeSingle();

      if (!project?.user_id) {
        logger.warn('Project not found or missing user_id for Jira issue types', { projectKey });
        return [];
      }

      const accessToken = await this.getAccessToken(project.user_id);
      const cloudId = await this.getCloudId(project.user_id);

      if (!accessToken || !cloudId) {
        logger.warn('Jira not connected, returning empty issue types', {
          userId: project.user_id,
        });
        return [];
      }

      const projectResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/project/${projectKey}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!projectResponse.ok) {
        logger.error('Failed to fetch Jira project', { 
          status: projectResponse.status, 
          projectKey 
        });
        return [];
      }

      const jiraProject = await projectResponse.json();
      const jiraProjectId = jiraProject.id;

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issuetype/project?projectId=${jiraProjectId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch Jira issue types', { 
          status: response.status, 
          projectKey,
          projectId: jiraProjectId
        });
        return [];
      }

      const issueTypes = await response.json();

      return issueTypes.map((type: any) => ({
        id: type.id,
        name: type.name,
        description: type.description || '',
        iconUrl: type.iconUrl || '',
        subtask: type.subtask || false,
        hierarchyLevel: type.hierarchyLevel || 0,
      }));
    } catch (error) {
      logger.error('Error getting Jira issue types', { error, projectKey });
      return [];
    }
  }

  /**
   * Get Jira sprints for project
   */
  static async getJiraSprints(projectKey: string): Promise<JiraSprint[]> {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('user_id, jira_project_id')
        .eq('jira_project_key', projectKey)
        .maybeSingle();

      if (!project?.user_id) {
        logger.warn('Project not found or missing user_id for Jira sprints', { projectKey });
        return [];
      }

      const accessToken = await this.getAccessToken(project.user_id);
      const cloudId = await this.getCloudId(project.user_id);

      if (!accessToken || !cloudId) {
        logger.warn('Jira not connected, returning empty sprints', {
          userId: project.user_id,
        });
        return [];
      }

      const boardsResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/board?projectKeyOrId=${projectKey}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!boardsResponse.ok) {
        logger.error('Failed to fetch Jira boards', { 
          status: boardsResponse.status, 
          projectKey 
        });
        return [];
      }

      const boardsData = await boardsResponse.json();
      const boards = boardsData.values || [];

      if (boards.length === 0) {
        logger.warn('No boards found for project', { projectKey });
        return [];
      }

      const boardId = boards[0].id;
      const sprintsResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/board/${boardId}/sprint?state=active,closed,future`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!sprintsResponse.ok) {
        logger.error('Failed to fetch Jira sprints', { 
          status: sprintsResponse.status, 
          boardId,
          projectKey 
        });
        return [];
      }

      const sprintsData = await sprintsResponse.json();
      const sprints = sprintsData.values || [];

      return sprints.map((sprint: any) => ({
        id: sprint.id,
        name: sprint.name,
        state: sprint.state || 'future',
        startDate: sprint.startDate,
        endDate: sprint.endDate,
        goal: sprint.goal || '',
      }));
    } catch (error) {
      logger.error('Error getting Jira sprints', { error, projectKey });
      return [];
    }
  }

  /**
   * Get sync status for a project
   */
  static async getSyncStatus(projectId: string): Promise<JiraSyncStatus> {
    try {
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select('status, jira_key')
        .eq('project_id', projectId)
        .not('jira_key', 'is', null);

      if (error) {
        logger.error('Failed to get sync status', { error });
        return {
          synced: false,
          lastSync: '',
          totalTickets: 0,
          todoTickets: 0,
          inProgressTickets: 0,
          doneTickets: 0,
          errors: [error.message]
        };
      }

      const totalTickets = tickets?.length || 0;
      const todoTickets = tickets?.filter(t => t.status === 'todo').length || 0;
      const inProgressTickets = tickets?.filter(t => t.status === 'in_progress').length || 0;
      const doneTickets = tickets?.filter(t => t.status === 'done').length || 0;

      return {
        synced: true,
        lastSync: new Date().toISOString(),
        totalTickets,
        todoTickets,
        inProgressTickets,
        doneTickets,
        errors: []
      };
    } catch (error) {
      logger.error('Error getting sync status', { error });
      return {
        synced: false,
        lastSync: '',
        totalTickets: 0,
        todoTickets: 0,
        inProgressTickets: 0,
        doneTickets: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  // ==========================================================================
  // Auto-Sync (from jiraAutoSyncService.ts)
  // ==========================================================================

  /**
   * Automatically sync agent execution completion to Jira
   */
  static async syncAgentCompletion(
    options: JiraSyncOptions & {
      phaseName: string;
      agentName: string;
      filesGenerated: number;
      tokensUsed?: number;
      prUrl?: string;
      branchName?: string;
      duration?: number; // in seconds
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('jira_key, jira_ticket_id')
        .eq('id', options.ticketId)
        .single();

      const jiraKey = ticket?.jira_key || ticket?.jira_ticket_id;
      if (!jiraKey) {
        logger.warn('Ticket has no Jira key, skipping sync', { ticketId: options.ticketId });
        return { success: false, error: 'Ticket has no Jira key' };
      }

      const results = await Promise.allSettled([
        // 1. Add comment with activity details
        options.addComment !== false
          ? this.addActivityComment(options.userId, jiraKey, {
              phaseName: options.phaseName,
              agentName: options.agentName,
              filesGenerated: options.filesGenerated,
              tokensUsed: options.tokensUsed,
              prUrl: options.prUrl,
              branchName: options.branchName,
            })
          : Promise.resolve({ success: true }),

        // 2. Transition status if needed
        options.autoTransition !== false
          ? this.autoTransitionStatus(options.userId, jiraKey, options.ticketId, options.phaseName)
          : Promise.resolve({ success: true }),

        // 3. Log work (time tracking)
        options.logWork !== false && options.duration
          ? this.logWorkTime(options.userId, jiraKey, {
              agentName: options.agentName,
              duration: options.duration,
              description: `Completed ${options.phaseName} phase`,
            })
          : Promise.resolve({ success: true }),
      ]);

      const failures = results.filter((r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success));
      if (failures.length > 0) {
        logger.warn('Some Jira sync operations failed', {
          ticketId: options.ticketId,
          failures: failures.length,
        });
      }

      logger.info('Jira auto-sync completed', {
        ticketId: options.ticketId,
        jiraKey,
        commentAdded: results[0].status === 'fulfilled',
        statusTransitioned: results[1].status === 'fulfilled',
        workLogged: results[2].status === 'fulfilled',
      });

      return { success: true };
    } catch (error) {
      logger.error('Error in Jira auto-sync', { error, ticketId: options.ticketId });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Automatically sync phase start to Jira
   */
  static async syncPhaseStart(
    options: JiraSyncOptions & {
      phaseName: string;
      agentName: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('jira_key, jira_ticket_id')
        .eq('id', options.ticketId)
        .single();

      const jiraKey = ticket?.jira_key || ticket?.jira_ticket_id;
      if (!jiraKey) {
        return { success: false, error: 'Ticket has no Jira key' };
      }

      if (options.addComment !== false) {
        await this.addActivityComment(options.userId, jiraKey, {
          phaseName: options.phaseName,
          agentName: options.agentName,
          action: 'started',
        });
      }

      if (options.autoTransition !== false) {
        await this.ensureStatus(options.userId, jiraKey, options.ticketId, 'In Progress');
      }

      return { success: true };
    } catch (error) {
      logger.error('Error syncing phase start to Jira', { error, ticketId: options.ticketId });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Automatically sync PR creation to Jira
   */
  static async syncPullRequest(
    options: JiraSyncOptions & {
      prUrl: string;
      branchName: string;
      phaseName: string;
      fileCount: number;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('jira_key, jira_ticket_id')
        .eq('id', options.ticketId)
        .single();

      const jiraKey = ticket?.jira_key || ticket?.jira_ticket_id;
      if (!jiraKey) {
        return { success: false, error: 'Ticket has no Jira key' };
      }

      const comment = `🔗 **Pull Request Created**

**Phase**: ${options.phaseName}
**Branch**: \`${options.branchName}\`
**Files**: ${options.fileCount}

[View Pull Request](${options.prUrl})

*Automatically generated by Cyclone AI Platform*`;

      const result = await this.addComment(options.userId, jiraKey, comment);

      if (result.success) {
        await this.autoTransitionStatus(options.userId, jiraKey, options.ticketId, 'review');
      }

      return result;
    } catch (error) {
      logger.error('Error syncing PR to Jira', { error, ticketId: options.ticketId });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Add activity comment to Jira
   */
  private static async addActivityComment(
    userId: string,
    jiraKey: string,
    details: {
      phaseName: string;
      agentName: string;
      filesGenerated?: number;
      tokensUsed?: number;
      prUrl?: string;
      branchName?: string;
      action?: 'started' | 'completed';
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      let comment = '';

      if (details.action === 'started') {
        comment = `🤖 **Agent Activity**: ${details.agentName} started working on *${details.phaseName}* phase`;
      } else {
        comment = `✅ **Agent Activity**: ${details.agentName} completed *${details.phaseName}* phase`;

        if (details.filesGenerated !== undefined) {
          comment += `\n\n📁 **Files Generated**: ${details.filesGenerated}`;
        }

        if (details.tokensUsed !== undefined) {
          comment += `\n💰 **AI Tokens Used**: ${details.tokensUsed.toLocaleString()}`;
        }

        if (details.prUrl) {
          comment += `\n\n🔗 [View Pull Request](${details.prUrl})`;
        }

        if (details.branchName) {
          comment += `\n🌿 **Branch**: \`${details.branchName}\``;
        }
      }

      comment += `\n\n*Automatically synced from Cyclone AI Platform*`;

      return await this.addComment(userId, jiraKey, comment);
    } catch (error) {
      logger.error('Error adding activity comment', { error });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Automatically transition Jira ticket status based on phase completion
   */
  private static async autoTransitionStatus(
    userId: string,
    jiraKey: string,
    ticketId: string,
    phaseNameOrTargetStatus: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('status')
        .eq('id', ticketId)
        .single();

      if (!ticket) {
        return { success: false, error: 'Ticket not found' };
      }

      let targetStatus: string;
      if (phaseNameOrTargetStatus === 'review' || phaseNameOrTargetStatus === 'In Review') {
        targetStatus = 'In Review';
      } else if (ticket.status === 'todo') {
        targetStatus = 'In Progress';
      } else {
        targetStatus = ticket.status;
      }

      const transitionsResult = await this.getTransitions(userId, jiraKey);
      if (!transitionsResult.success || !transitionsResult.data) {
        return { success: false, error: 'Failed to get transitions' };
      }

      const targetTransition = transitionsResult.data.find(
        (t) => t.name.toLowerCase() === targetStatus.toLowerCase()
      );

      if (!targetTransition) {
        const fuzzyMatch = transitionsResult.data.find((t) =>
          t.name.toLowerCase().includes(targetStatus.toLowerCase()) ||
          targetStatus.toLowerCase().includes(t.name.toLowerCase())
        );

        if (fuzzyMatch) {
          const result = await this.transitionIssue(userId, jiraKey, fuzzyMatch.id);
          if (result.success) {
            await supabase
              .from('tickets')
              .update({ status: this.mapJiraStatusToCyclone(fuzzyMatch.name) })
              .eq('id', ticketId);
          }
          return result;
        }

        logger.warn('No matching transition found', { targetStatus, available: transitionsResult.data.map((t) => t.name) });
        return { success: false, error: `No transition found for status: ${targetStatus}` };
      }

      const result = await this.transitionIssue(userId, jiraKey, targetTransition.id);
      if (result.success) {
        await supabase
          .from('tickets')
          .update({ status: this.mapJiraStatusToCyclone(targetStatus) })
          .eq('id', ticketId);
      }

      return result;
    } catch (error) {
      logger.error('Error auto-transitioning Jira status', { error });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Ensure ticket is in specific status (transition if needed)
   */
  private static async ensureStatus(
    userId: string,
    jiraKey: string,
    ticketId: string,
    targetStatus: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: ticket } = await supabase
        .from('tickets')
        .select('status')
        .eq('id', ticketId)
        .single();

      if (!ticket) {
        return { success: false, error: 'Ticket not found' };
      }

      if (this.mapCycloneStatusToJira(ticket.status) === targetStatus) {
        return { success: true };
      }

      return await this.autoTransitionStatus(userId, jiraKey, ticketId, targetStatus);
    } catch (error) {
      logger.error('Error ensuring Jira status', { error });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Log work time to Jira
   */
  private static async logWorkTime(
    userId: string,
    jiraKey: string,
    details: {
      agentName: string;
      duration: number; // in seconds
      description: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const minutes = Math.round(details.duration / 60);
      const timeSpent = minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

      const accessToken = await this.getAccessToken(userId);
      const cloudId = await this.getCloudId(userId);

      if (!accessToken || !cloudId) {
        return { success: false, error: 'Jira not connected' };
      }

      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue/${jiraKey}/worklog`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timeSpent,
            comment: {
              type: 'doc',
              version: 1,
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: `${details.description} by ${details.agentName}`,
                    },
                  ],
                },
              ],
            },
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        logger.error('Failed to log work time', { error });
        return { success: false, error: 'Failed to log work time' };
      }

      logger.info('Work time logged to Jira', { jiraKey, timeSpent, agentName: details.agentName });
      return { success: true };
    } catch (error) {
      logger.error('Error logging work time', { error });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Note: mapJiraStatusToCyclone is already defined above (line 1530)

  // ==========================================================================
  // Project Plan Management (from jiraBidirectionalSyncService.ts)
  // ==========================================================================

  /**
   * Push complete project plan to Jira
   */
  static async pushProjectPlan(options: PushProjectPlanOptions): Promise<{
    success: boolean;
    createdEpics: string[];
    createdSprints: number[];
    createdTickets: string[];
    errors: string[];
  }> {
    try {
      logger.info('Pushing project plan to Jira', { projectId: options.projectId });

      const createdEpics: string[] = [];
      const createdSprints: number[] = [];
      const createdTickets: string[] = [];
      const errors: string[] = [];

      // Get project's Jira connection
      const { data: project } = await supabase
        .from('projects')
        .select('jira_project_key')
        .eq('id', options.projectId)
        .maybeSingle();

      if (!project?.jira_project_key) {
        return {
          success: false,
          createdEpics: [],
          createdSprints: [],
          createdTickets: [],
          errors: ['No Jira project linked to this Cyclone project']
        };
      }

      // Create epics
      for (const epic of options.epics) {
        try {
          const epicKey = await this.createJiraEpic(project.jira_project_key, epic);
          if (epicKey) {
            createdEpics.push(epicKey);
          }
        } catch (error) {
          errors.push(`Failed to create epic "${epic.name}": ${error}`);
        }
      }

      // Create sprints
      for (const sprint of options.sprints) {
        try {
          const sprintId = await this.createJiraSprint(project.jira_project_key, sprint);
          if (sprintId) {
            createdSprints.push(sprintId);
          }
        } catch (error) {
          errors.push(`Failed to create sprint "${sprint.name}": ${error}`);
        }
      }

      // Create tickets
      for (const ticket of options.tickets) {
        try {
          const ticketKey = await this.createJiraTicket(project.jira_project_key, ticket, {
            epicKey: createdEpics[0], // Link to first epic if available
            sprintId: createdSprints[0] // Link to first sprint if available
          });
          if (ticketKey) {
            createdTickets.push(ticketKey);
          }
        } catch (error) {
          errors.push(`Failed to create ticket "${ticket.summary}": ${error}`);
        }
      }

      const success = errors.length === 0;

      logger.info('Project plan push completed', { 
        projectId: options.projectId,
        success,
        createdEpics: createdEpics.length,
        createdSprints: createdSprints.length,
        createdTickets: createdTickets.length,
        errors: errors.length
      });

      return {
        success,
        createdEpics,
        createdSprints,
        createdTickets,
        errors
      };
    } catch (error) {
      logger.error('Error pushing project plan to Jira', { error, projectId: options.projectId });
      return {
        success: false,
        createdEpics: [],
        createdSprints: [],
        createdTickets: [],
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  /**
   * Create Jira epic (production-ready implementation)
   */
  private static async createJiraEpic(projectKey: string, epic: any): Promise<string | null> {
    try {
      // Get project to find userId
      const { data: project } = await supabase
        .from('projects')
        .select('user_id, jira_project_id')
        .eq('jira_project_key', projectKey)
        .maybeSingle();

      if (!project?.user_id) {
        logger.error('Project not found or missing user_id for creating Jira epic', { projectKey });
        return null;
      }

      // Get Jira connection
      const { data: connection } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('user_id', project.user_id)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (!connection?.access_token || !connection.metadata?.cloudId) {
        logger.error('Jira not connected, cannot create epic', {
          userId: project.user_id,
        });
        return null;
      }

      const cloudId = connection.metadata.cloudId;
      const accessToken = connection.access_token;

      // Create epic via Jira API
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              project: { key: projectKey },
              summary: epic.name,
              description: {
                type: 'doc',
                version: 1,
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      {
                        type: 'text',
                        text: epic.description || '',
                      },
                    ],
                  },
                ],
              },
              issuetype: { name: 'Epic' },
              priority: { name: epic.priority || 'Medium' },
              customfield_10014: epic.name, // Epic name field
            },
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Failed to create Jira epic', {
          status: response.status,
          error: errorText,
          epicName: epic.name,
          projectKey,
        });
        return null;
      }

      const createdEpic = await response.json();
      logger.info('Jira epic created successfully', {
        epicKey: createdEpic.key,
        name: epic.name,
        projectKey,
      });

      return createdEpic.key;
    } catch (error) {
      logger.error('Error creating Jira epic', { error, name: epic.name, projectKey });
      return null;
    }
  }

  /**
   * Create Jira sprint
   */
  private static async createJiraSprint(projectKey: string, sprint: any): Promise<number | null> {
    try {
      // Get project to find userId
      const { data: project } = await supabase
        .from('projects')
        .select('user_id, jira_project_id')
        .eq('jira_project_key', projectKey)
        .maybeSingle();

      if (!project?.user_id) {
        logger.error('Project not found or missing user_id for creating Jira sprint', { projectKey });
        return null;
      }

      // Get Jira connection
      const { data: connection } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('user_id', project.user_id)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (!connection?.access_token || !connection.metadata?.cloudId) {
        logger.error('Jira not connected, cannot create sprint', {
          userId: project.user_id,
        });
        return null;
      }

      const cloudId = connection.metadata.cloudId;
      const accessToken = connection.access_token;

      // Get board for project (Jira Agile API requires board ID)
      const boardsResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/board?projectKeyOrId=${projectKey}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
          },
        }
      );

      if (!boardsResponse.ok) {
        logger.error('Failed to fetch Jira boards for sprint creation', { 
          status: boardsResponse.status, 
          projectKey 
        });
        return null;
      }

      const boardsData = await boardsResponse.json();
      const boards = boardsData.values || [];

      if (boards.length === 0) {
        logger.error('No boards found for project, cannot create sprint', { projectKey });
        return null;
      }

      const boardId = boards[0].id;

      // Create sprint via Jira Agile API
      const createResponse = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/sprint`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: sprint.name,
            goal: sprint.goal || '',
            startDate: sprint.startDate || new Date().toISOString(),
            endDate: sprint.endDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            originBoardId: boardId,
          }),
        }
      );

      if (!createResponse.ok) {
        const errorText = await createResponse.text();
        logger.error('Failed to create Jira sprint', { 
          status: createResponse.status,
          error: errorText,
          sprintName: sprint.name,
          boardId
        });
        return null;
      }

      const createdSprint = await createResponse.json();
      logger.info('Jira sprint created successfully', { 
        sprintId: createdSprint.id, 
        name: sprint.name,
        boardId
      });
      
      return createdSprint.id;
    } catch (error) {
      logger.error('Error creating Jira sprint', { error, name: sprint.name, projectKey });
      return null;
    }
  }

  /**
   * Create Jira ticket
   */
  private static async createJiraTicket(projectKey: string, ticket: any, context: any): Promise<string | null> {
    try {
      // Get project to find userId
      const { data: project } = await supabase
        .from('projects')
        .select('user_id, jira_project_id')
        .eq('jira_project_key', projectKey)
        .maybeSingle();

      if (!project?.user_id) {
        logger.error('Project not found or missing user_id for creating Jira ticket', { projectKey });
        return null;
      }

      // Get Jira connection
      const { data: connection } = await supabase
        .from('user_connections')
        .select('access_token, metadata')
        .eq('user_id', project.user_id)
        .eq('provider', 'jira')
        .eq('status', 'active')
        .maybeSingle();

      if (!connection?.access_token || !connection.metadata?.cloudId) {
        logger.error('Jira not connected, cannot create ticket', {
          userId: project.user_id,
        });
        return null;
      }

      const cloudId = connection.metadata.cloudId;
      const accessToken = connection.access_token;

      // Build issue fields
      const fields: any = {
        project: { key: projectKey },
        summary: ticket.summary,
        description: {
          type: 'doc',
          version: 1,
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: ticket.description || '',
                },
              ],
            },
          ],
        },
        issuetype: { name: ticket.issueType || 'Task' },
      };

      // Add priority if provided
      if (ticket.priority) {
        fields.priority = { name: ticket.priority };
      }

      // Add story points if provided
      if (ticket.storyPoints) {
        fields.customfield_10016 = ticket.storyPoints;
      }

      // Add epic link if provided
      if (context?.epicKey) {
        fields.customfield_10014 = context.epicKey;
      }

      // Create ticket via Jira API
      const response = await fetch(
        `https://api.atlassian.com/ex/jira/${cloudId}/rest/api/3/issue`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fields }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Failed to create Jira ticket', {
          status: response.status,
          error: errorText,
          summary: ticket.summary,
          projectKey,
        });
        return null;
      }

      const createdIssue = await response.json();
      const ticketKey = createdIssue.key;

      // Move ticket to sprint if provided (using Jira API directly)
      if (context?.sprintId && ticketKey) {
        try {
          const sprintIdNum = Number(context.sprintId);

          const sprintResponse = await fetch(
            `https://api.atlassian.com/ex/jira/${cloudId}/rest/agile/1.0/sprint/${sprintIdNum}/issue`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                issues: [ticketKey],
              }),
            }
          );

          if (!sprintResponse.ok) {
            logger.warn('Failed to move ticket to sprint, but ticket was created', {
              ticketKey,
              sprintId: context.sprintId,
            });
          } else {
            logger.info('Ticket moved to sprint', { ticketKey, sprintId: context.sprintId });
          }
        } catch (error) {
          logger.warn('Failed to move ticket to sprint, but ticket was created', {
            error,
            ticketKey,
            sprintId: context.sprintId,
          });
        }
      }

      logger.info('Jira ticket created successfully', {
        ticketKey,
        summary: ticket.summary,
        projectKey,
      });

      return ticketKey;
    } catch (error) {
      logger.error('Error creating Jira ticket', { error, summary: ticket.summary, projectKey });
      return null;
    }
  }
}
